import { getLocaleEndDateString } from '@common/lib/getLocaleDate';
import { isGiftCardFixedTicketConfig } from '@console/common/project/giftCardTicketConfig';
import type {
  APITablePropsMappedFromDispatch,
  APITablePropsMappedFromState
} from '@console/containers/project/brandCheck/APITableContainer';
import type { LocalGiftCardConfig } from '@console/reducers/projects';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import type { Theme } from '@mui/material';
import type { StyleRules, WithStyles } from '@mui/styles';
import withStyles from '@mui/styles/withStyles';
import { Fragment } from 'react';
import GifteeBoxConfigRow from './APITable/GifteeBoxConfigRow';
import GiftCardTicketConfigRow from './GiftCardTicketConfigRow';

const styles = (theme: Theme): StyleRules => ({
  tableCell: {
    paddingRight: theme.spacing(4),
    verticalAlign: 'middle'
  },
  contentCell: {
    paddingRight: theme.spacing(2),
    width: 420
  },
  issuableDuration: {
    margin: `${theme.spacing()} 0`
  }
});

type APITableProps = APITablePropsMappedFromDispatch &
  APITablePropsMappedFromState &
  WithStyles<typeof styles>;

const APITable: React.FC<APITableProps> = ({
  classes,
  giftCardConfigs,
  gifteeBoxConfigs
}) => {
  const getFirstColumnRowSpan = (giftCardConfig: LocalGiftCardConfig): number =>
    giftCardConfig.giftCardTicketConfigs.reduce(
      (total, giftCardTicketConfig) => {
        if (isGiftCardFixedTicketConfig(giftCardTicketConfig)) {
          return total + 1;
        }
        return (
          total + giftCardTicketConfig.choosableContentOption.choices.length
        );
      },
      1
    );

  return (
    <Table className={classes.table} data-cy="apiTable">
      <TableHead>
        <TableRow>
          <TableCell className={classes.tableCell} align="left">
            発行有効期限
          </TableCell>
          <TableCell className={classes.tableCell} align="left">
            ギフトのタイプ
          </TableCell>
          <TableCell className={classes.tableCell} align="left">
            ブランド
          </TableCell>
          <TableCell className={classes.contentCell} align="left">
            ギフト
          </TableCell>
          <TableCell className={classes.tableCell} align="right">
            審査ステータス
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {giftCardConfigs
          ? giftCardConfigs.map((giftCardConfig, giftCardConfigIndex) => (
              <Fragment key={`giftCardConfig-${giftCardConfigIndex}`}>
                <TableRow data-test="giftConfigRow">
                  <TableCell
                    className={classes.tableCell}
                    align="left"
                    rowSpan={getFirstColumnRowSpan(giftCardConfig)}
                    data-cy={`apiGiftCardIssueConfigs-${giftCardConfigIndex}`}
                  >
                    {giftCardConfig.apiGiftCardIssueConfigs.map(
                      (apiGiftCardIssueConfig, apiGiftCardIssueConfigIndex) => (
                        <div
                          key={`apiGiftCardIssueConfig-${giftCardConfigIndex}-${apiGiftCardIssueConfigIndex}`}
                          className={classes.issuableDuration}
                        >
                          <Typography
                            variant="body2"
                            data-cy={`apiGiftCardIssueConfigIssuableEndAt-${giftCardConfigIndex}-${apiGiftCardIssueConfigIndex}`}
                          >
                            {getLocaleEndDateString(
                              apiGiftCardIssueConfig.issuableEndAt
                            )}
                          </Typography>
                        </div>
                      )
                    )}
                  </TableCell>
                </TableRow>
                {giftCardConfig.giftCardTicketConfigs.map(
                  (giftCardTicketConfig, giftCardTicketConfigIndex) => (
                    <GiftCardTicketConfigRow
                      key={`giftCardTicketConfigRow-${giftCardConfigIndex}-${giftCardTicketConfigIndex}`}
                      data-cy={`giftCardTicketConfigRow-${giftCardConfigIndex}-${giftCardTicketConfigIndex}`}
                      giftCardTicketConfig={giftCardTicketConfig}
                    />
                  )
                )}
              </Fragment>
            ))
          : null}
        {gifteeBoxConfigs
          ? gifteeBoxConfigs.map(gifteeBoxConfig => (
              <GifteeBoxConfigRow
                key={gifteeBoxConfig.urlCode}
                gifteeBoxConfig={gifteeBoxConfig}
              />
            ))
          : null}
      </TableBody>
    </Table>
  );
};

export default withStyles(styles)(APITable);
